import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, RefreshCw, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { billingService, HostBalance, EventWithTransactions, BillingTransaction } from '../../services/billingService';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../../components/UI/Button';
import { BalanceDisplay, EventTransactionCard, RecentActivities } from '../../components/Billing';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { logError } from '../../utils/errorLogger';

const HostBalancePage: React.FC = () => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  
  const [balance, setBalance] = useState<HostBalance | null>(null);
  const [events, setEvents] = useState<EventWithTransactions[]>([]);
  const [recentActivities, setRecentActivities] = useState<BillingTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // 20 events per page
  const [pagination, setPagination] = useState<{
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  } | null>(null);

  // Load balance and events with transactions
  const loadData = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      const [balanceData, eventsData] = await Promise.all([
        billingService.getMyBalance(),
        billingService.getEventsWithTransactions(page, pageSize)
      ]);
      setBalance(balanceData);
      setEvents(eventsData.events);
      setRecentActivities(eventsData.recent_activities);
      if (eventsData.pagination) {
        setPagination(eventsData.pagination);
      }
    } catch (error: any) {
      logError('Failed to load balance data', error);
      showNotification('Failed to load balance information', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification, pageSize]);

  const refreshData = useCallback(async () => {
    try {
      setDataLoading(true);
      const [balanceData, eventsData] = await Promise.all([
        billingService.getMyBalance(),
        billingService.getEventsWithTransactions(currentPage, pageSize)
      ]);
      setBalance(balanceData);
      setEvents(eventsData.events);
      setRecentActivities(eventsData.recent_activities);
      if (eventsData.pagination) {
        setPagination(eventsData.pagination);
      }
    } catch (error: any) {
      logError('Failed to refresh data', error);
    } finally {
      setDataLoading(false);
    }
  }, [currentPage, pageSize]);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    loadData(newPage);
  }, [loadData]);

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className={`rounded-lg border-2 p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="text-center py-12">
            <Wallet className={`h-12 w-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Unable to load balance information
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            My Balance
          </h1>
          <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            View your account balance and transaction history
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refreshData}
          loading={dataLoading}
          leftIcon={<RefreshCw className="h-4 w-4" />}
        >
          Refresh
        </Button>
      </div>

      {/* Balance Display */}
      <BalanceDisplay balance={balance} showDetails={false} />

      {/* Events with Transactions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Events
            </h2>
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {pagination ? pagination.total_count : events.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshData}
            loading={dataLoading}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        </div>
        
        {events.length === 0 ? (
          <div className={`rounded-lg border p-8 text-center ${
            theme === 'dark' 
              ? 'bg-gray-900/80 border-gray-700/50' 
              : 'bg-white border-gray-200'
          }`}>
            <Calendar className={`h-12 w-12 mx-auto mb-4 ${
              theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
            }`} />
            <p className={`text-lg font-medium ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              No events found
            </p>
            <p className={`text-sm mt-2 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Your events and their transactions will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(() => {
              // Group events by series
              const standaloneEvents: EventWithTransactions[] = [];
              const seriesGroups: Map<string, { main: EventWithTransactions; subEvents: EventWithTransactions[] }> = new Map();
              
              events.forEach((event) => {
                if (event.series_id) {
                  // Event is part of a series
                  if (event.is_main_event_in_series) {
                    // This is the main event
                    if (!seriesGroups.has(event.series_id)) {
                      seriesGroups.set(event.series_id, { main: event, subEvents: [] });
                    } else {
                      // Update main event if already exists
                      seriesGroups.get(event.series_id)!.main = event;
                    }
                  } else {
                    // This is a sub-event
                    if (!seriesGroups.has(event.series_id)) {
                      seriesGroups.set(event.series_id, { main: null as any, subEvents: [event] });
                    } else {
                      seriesGroups.get(event.series_id)!.subEvents.push(event);
                    }
                  }
                } else {
                  // Standalone event
                  standaloneEvents.push(event);
                }
              });
              
              // Sort sub-events by creation date
              seriesGroups.forEach((group) => {
                group.subEvents.sort((a, b) => 
                  new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
              });
              
              // Render events: standalone first, then series groups
              return (
                <>
                  {standaloneEvents.map((event) => (
                    <EventTransactionCard key={event.id} event={event} />
                  ))}
                  {Array.from(seriesGroups.values()).map((group) => (
                    <div key={group.main?.id || group.subEvents[0]?.series_id}>
                      {group.main && (
                        <EventTransactionCard key={group.main.id} event={group.main} />
                      )}
                      {group.subEvents.map((subEvent) => (
                        <div key={subEvent.id} className={`ml-6 border-l-2 ${
                          theme === 'dark' ? 'border-gray-700' : 'border-gray-300'
                        }`}>
                          <EventTransactionCard event={subEvent} />
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 rounded-lg border p-4 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-sm text-center sm:text-left ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} events
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_previous || loading || dataLoading}
              className="flex-1 sm:flex-initial"
              leftIcon={<ChevronLeft className="h-4 w-4" />}
            >
              Previous
            </Button>
            <span className={`px-2 sm:px-4 py-2 text-sm whitespace-nowrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next || loading || dataLoading}
              className="flex-1 sm:flex-initial"
              rightIcon={<ChevronRight className="h-4 w-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {recentActivities.length > 0 && (
        <div>
          <RecentActivities activities={recentActivities} loading={dataLoading} />
        </div>
      )}
    </div>
  );
};

export default HostBalancePage;

